import {
  BOARD_HEIGHT,
  BOARD_WIDTH,
  DEFAULT_PEN,
  MAX_PEN,
  MIN_PEN,
  type ChatMessage,
  type ClientMessage,
  type GifResult,
  type ServerMessage,
  type User,
} from "./protocol.ts";

// ---------------------------------------------------------------------------
// DOM
// ---------------------------------------------------------------------------

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (node === null) throw new Error(`Element #${id} niet gevonden`);
  return node as T;
}

const canvas = el<HTMLCanvasElement>("board");
const frame = el<HTMLDivElement>("board-frame");
const stage = el<HTMLDivElement>("stage");
const loginOverlay = el<HTMLDivElement>("login");
const loginForm = el<HTMLFormElement>("login-form");
const nameInput = el<HTMLInputElement>("name");
const penInput = el<HTMLInputElement>("pen");
const penPreview = el<HTMLSpanElement>("pen-preview");
const clearMineButton = el<HTMLButtonElement>("clear-mine");
const clearAllButton = el<HTMLButtonElement>("clear-all");
const userList = el<HTMLUListElement>("user-list");
const userCount = el<HTMLSpanElement>("user-count");
const connectionBadge = el<HTMLSpanElement>("connection");
const promptLabel = el<HTMLSpanElement>("prompt");
const promptRefresh = el<HTMLButtonElement>("prompt-refresh");
const soundToggle = el<HTMLButtonElement>("sound-toggle");
const chatPanel = el<HTMLElement>("chat-panel");
const chatToggle = el<HTMLButtonElement>("chat-toggle");
const chatClose = el<HTMLButtonElement>("chat-close");
const chatLog = el<HTMLUListElement>("chat-log");
const chatForm = el<HTMLFormElement>("chat-form");
const chatInput = el<HTMLInputElement>("chat-input");
const gifToggle = el<HTMLButtonElement>("gif-toggle");
const gifPicker = el<HTMLDivElement>("gif-picker");
const gifSearch = el<HTMLInputElement>("gif-search");
const gifStatus = el<HTMLParagraphElement>("gif-status");
const gifResults = el<HTMLDivElement>("gif-results");

function require2dContext(target: HTMLCanvasElement): CanvasRenderingContext2D {
  const context = target.getContext("2d");
  if (context === null) throw new Error("Canvas 2D-context niet beschikbaar");
  return context;
}

const ctx = require2dContext(canvas);

// ---------------------------------------------------------------------------
// Lokale tekenstate
// ---------------------------------------------------------------------------

interface Stroke {
  userId: string;
  color: string;
  width: number;
  points: { x: number; y: number }[];
}

/** Alles wat er op het bord staat, van iedereen. */
const strokes = new Map<string, Stroke>();

let me: User | null = null;
let socket: WebSocket | null = null;
let penWidth = DEFAULT_PEN;

// Actieve stroke van deze gebruiker.
let activePointerId: number | null = null;
let activeStrokeKey: string | null = null;
let activeStrokeId = "";
let lastSentPoint: { x: number; y: number } | null = null;
let strokeCounter = 0;

/** Minimale afstand (in board-eenheden) tussen twee verstuurde punten. */
const MIN_POINT_DISTANCE = 1.5;

// ---------------------------------------------------------------------------
// Canvasformaat: vaste logische ruimte, scherpe weergave op elk scherm
// ---------------------------------------------------------------------------

let scale = 1;

/** Het bord houdt altijd de 16:9-verhouding, zodat iedereen hetzelfde beeld ziet. */
function layout(): void {
  const available = stage.getBoundingClientRect();
  if (available.width === 0 || available.height === 0) return;

  const width = Math.min(available.width, (available.height * BOARD_WIDTH) / BOARD_HEIGHT);
  const height = (width * BOARD_HEIGHT) / BOARD_WIDTH;
  frame.style.width = `${width}px`;
  frame.style.height = `${height}px`;

  const dpr = window.devicePixelRatio || 1;
  const pixelWidth = Math.round(width * dpr);
  const pixelHeight = Math.round(height * dpr);
  const resized = canvas.width !== pixelWidth || canvas.height !== pixelHeight;
  if (resized) {
    canvas.width = pixelWidth;
    canvas.height = pixelHeight;
  }
  scale = pixelWidth / BOARD_WIDTH;
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
  // Een canvas wist zichzelf bij een nieuw formaat, dus alles opnieuw opbouwen.
  if (resized) redrawAll();
}

new ResizeObserver(layout).observe(stage);
window.addEventListener("resize", layout);

/** Schermcoördinaten -> logische board-coördinaten. */
function toBoard(event: { clientX: number; clientY: number }): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  return {
    x: ((event.clientX - rect.left) / rect.width) * BOARD_WIDTH,
    y: ((event.clientY - rect.top) / rect.height) * BOARD_HEIGHT,
  };
}

// ---------------------------------------------------------------------------
// Renderen
//
// Strokes blijven staan tot iemand het bord wist, dus er is geen animatielus
// nodig: nieuwe segmenten worden er direct bij getekend. Alleen bij formaat-
// wijziging, historie of wissen wordt alles opnieuw opgebouwd.
// ---------------------------------------------------------------------------

function penStyle(stroke: Stroke): void {
  ctx.strokeStyle = stroke.color;
  ctx.lineWidth = stroke.width;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
}

/** Een losse tik zonder beweging: een rondje ter dikte van de pen. */
function drawDot(stroke: Stroke, point: { x: number; y: number }): void {
  ctx.fillStyle = stroke.color;
  ctx.beginPath();
  ctx.arc(point.x, point.y, stroke.width / 2, 0, Math.PI * 2);
  ctx.fill();
}

function drawStroke(stroke: Stroke): void {
  const points = stroke.points;
  if (points.length === 0) return;
  if (points.length === 1) {
    drawDot(stroke, points[0]!);
    return;
  }
  penStyle(stroke);
  ctx.beginPath();
  ctx.moveTo(points[0]!.x, points[0]!.y);
  for (let i = 1; i < points.length; i++) ctx.lineTo(points[i]!.x, points[i]!.y);
  ctx.stroke();
}

/** Alleen het laatst toegevoegde stukje bijtekenen. */
function drawLastSegment(stroke: Stroke): void {
  const points = stroke.points;
  if (points.length < 2) {
    if (points.length === 1) drawDot(stroke, points[0]!);
    return;
  }
  penStyle(stroke);
  ctx.beginPath();
  ctx.moveTo(points[points.length - 2]!.x, points[points.length - 2]!.y);
  ctx.lineTo(points[points.length - 1]!.x, points[points.length - 1]!.y);
  ctx.stroke();
}

function redrawAll(): void {
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.restore();
  for (const stroke of strokes.values()) drawStroke(stroke);
}

// ---------------------------------------------------------------------------
// Invoer
// ---------------------------------------------------------------------------

function sendMessage(message: ClientMessage): void {
  if (socket !== null && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(message));
  }
}

function beginStroke(x: number, y: number): void {
  if (me === null) return;
  activeStrokeId = String(strokeCounter++);
  activeStrokeKey = `local:${activeStrokeId}`;
  const stroke: Stroke = {
    userId: me.id,
    color: me.color,
    width: penWidth,
    points: [{ x, y }],
  };
  strokes.set(activeStrokeKey, stroke);
  drawDot(stroke, { x, y });
  lastSentPoint = { x, y };
  sendMessage({ t: "start", id: activeStrokeId, x, y, w: penWidth });
}

function extendStroke(x: number, y: number): void {
  if (activeStrokeKey === null) return;
  const stroke = strokes.get(activeStrokeKey);
  if (stroke === undefined) return;
  if (lastSentPoint !== null) {
    const dx = x - lastSentPoint.x;
    const dy = y - lastSentPoint.y;
    if (dx * dx + dy * dy < MIN_POINT_DISTANCE * MIN_POINT_DISTANCE) return;
  }
  stroke.points.push({ x, y });
  drawLastSegment(stroke);
  lastSentPoint = { x, y };
  sendMessage({ t: "point", id: activeStrokeId, x, y });
}

function endStroke(): void {
  if (activeStrokeKey === null) return;
  // De lijn staat al op het canvas; de server hoeft alleen te weten dat hij af is.
  sendMessage({ t: "end", id: activeStrokeId });
  activeStrokeKey = null;
  activePointerId = null;
  lastSentPoint = null;
}

canvas.addEventListener("pointerdown", (event) => {
  if (me === null || activePointerId !== null || !event.isPrimary) return;
  canvas.setPointerCapture(event.pointerId);
  activePointerId = event.pointerId;
  const { x, y } = toBoard(event);
  beginStroke(x, y);
});

canvas.addEventListener("pointermove", (event) => {
  if (event.pointerId !== activePointerId) return;
  event.preventDefault();
  // Tussenliggende punten meenemen: vloeiender bij snelle bewegingen.
  const events = event.getCoalescedEvents?.() ?? [];
  for (const point of events.length > 0 ? events : [event]) {
    const { x, y } = toBoard(point);
    extendStroke(x, y);
  }
});

for (const type of ["pointerup", "pointercancel"] as const) {
  canvas.addEventListener(type, (event) => {
    if (event.pointerId !== activePointerId) return;
    endStroke();
  });
}

// ---------------------------------------------------------------------------
// Pendikte
// ---------------------------------------------------------------------------

function updatePen(): void {
  penWidth = Math.min(MAX_PEN, Math.max(MIN_PEN, Number(penInput.value)));
  const size = Math.max(4, Math.min(penWidth, 44));
  penPreview.style.width = `${size}px`;
  penPreview.style.height = `${size}px`;
  if (me !== null) penPreview.style.backgroundColor = me.color;
}

penInput.addEventListener("input", updatePen);
updatePen();

clearMineButton.addEventListener("click", () => sendMessage({ t: "clear", scope: "mine" }));
// "alles" haalt ook het werk van anderen weg.
clearAllButton.addEventListener("click", () => sendMessage({ t: "clear", scope: "all" }));

// ---------------------------------------------------------------------------
// Opdracht van de dag
// ---------------------------------------------------------------------------

promptRefresh.addEventListener("click", () => {
  sendMessage({ t: "newPrompt" });
  // Kort meedraaien geeft feedback, ook als het antwoord een fractie later komt.
  promptRefresh.animate([{ transform: "rotate(0deg)" }, { transform: "rotate(360deg)" }], {
    duration: 500,
    easing: "ease-out",
  });
});

// ---------------------------------------------------------------------------
// Geluid bij binnenkomst
// ---------------------------------------------------------------------------

const SOUND_PREFERENCE_KEY = "team-draw:geluid";
/** Bij een golf aan binnenkomsten niet twintig keer over elkaar heen toeteren. */
const SOUND_COOLDOWN_MS = 1200;

const joinSound = new Audio("/sounds/join.mp3");
joinSound.volume = 0.5;
joinSound.preload = "auto";

let soundOn = localStorage.getItem(SOUND_PREFERENCE_KEY) !== "uit";
let lastSoundAt = 0;

function applySoundPreference(): void {
  soundToggle.textContent = soundOn ? "🔊" : "🔇";
  soundToggle.setAttribute("aria-pressed", String(soundOn));
}

function playJoinSound(): void {
  if (!soundOn) return;
  const now = Date.now();
  if (now - lastSoundAt < SOUND_COOLDOWN_MS) return;
  lastSoundAt = now;
  joinSound.currentTime = 0;
  // De browser mag dit weigeren als er nog niet geklikt is; dan gewoon stil blijven.
  void joinSound.play().catch(() => {});
}

soundToggle.addEventListener("click", () => {
  soundOn = !soundOn;
  localStorage.setItem(SOUND_PREFERENCE_KEY, soundOn ? "aan" : "uit");
  applySoundPreference();
  if (soundOn) playJoinSound();
});

applySoundPreference();

// ---------------------------------------------------------------------------
// Gebruikerslijst
// ---------------------------------------------------------------------------

function renderUsers(users: User[]): void {
  userCount.textContent = String(users.length);
  userList.replaceChildren(
    ...users.map((user) => {
      const item = document.createElement("li");
      item.className = "flex items-center gap-2";

      const dot = document.createElement("span");
      dot.className = "h-3 w-3 shrink-0 rounded-full ring-1 ring-slate-900/15";
      dot.style.backgroundColor = user.color;

      const label = document.createElement("span");
      // Op een telefoon is er alleen ruimte voor de bolletjes.
      label.className = "hidden truncate md:inline";
      label.textContent = user.name;
      if (me !== null && user.id === me.id) {
        label.className += " font-semibold";
        const suffix = document.createElement("span");
        suffix.className = "text-slate-400";
        suffix.textContent = " (jij)";
        label.append(suffix);
      }

      item.append(dot, label);
      return item;
    }),
  );
}

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

function appendChatMessage(message: ChatMessage): void {
  document.getElementById("chat-empty")?.remove();

  // Alleen meescrollen als je toch al onderaan zat, zodat terugleyen niet onderbroken wordt.
  const atBottom = chatLog.scrollHeight - chatLog.scrollTop - chatLog.clientHeight < 40;

  const item = document.createElement("li");
  item.className = "leading-snug break-words";

  const name = document.createElement("span");
  name.className = "font-semibold";
  name.style.color = message.color;
  name.textContent = message.name;
  item.append(name);

  if (message.text.length > 0) {
    const text = document.createElement("span");
    text.className = "text-slate-700";
    text.textContent = ` ${message.text}`;
    item.append(text);
  }

  if (message.gif !== undefined) {
    const image = document.createElement("img");
    image.src = message.gif.url;
    image.alt = message.gif.alt;
    image.loading = "lazy";
    image.referrerPolicy = "no-referrer";
    // Afmetingen vooraf zetten voorkomt dat de chat verspringt zodra de GIF binnen is.
    if (message.gif.width > 0 && message.gif.height > 0) {
      image.width = message.gif.width;
      image.height = message.gif.height;
    }
    // Hoge GIF's begrenzen, anders duwt één plaatje de hele chat uit beeld.
    image.className = "mt-1 block max-h-64 w-full rounded-lg bg-slate-100 object-contain";
    image.addEventListener("load", () => {
      if (atBottom) chatLog.scrollTop = chatLog.scrollHeight;
    });
    item.append(image);
  }

  chatLog.append(item);
  if (atBottom) chatLog.scrollTop = chatLog.scrollHeight;
}

/** Bij (her)verbinden stuurt de server de hele buffer: opnieuw opbouwen i.p.v. aanvullen. */
function renderChat(messages: ChatMessage[]): void {
  chatLog.replaceChildren();
  if (messages.length === 0) {
    const empty = document.createElement("li");
    empty.id = "chat-empty";
    empty.className = "text-sm text-slate-400";
    empty.textContent =
      "Nog niets gezegd. Ga los — de chat wordt geleegd zodra iedereen weg is, en elke dag.";
    chatLog.append(empty);
    return;
  }
  for (const message of messages) appendChatMessage(message);
  chatLog.scrollTop = chatLog.scrollHeight;
}

chatForm.addEventListener("submit", (event) => {
  event.preventDefault();
  const text = chatInput.value.trim();
  if (text.length === 0) return;
  sendMessage({ t: "chat", text });
  chatInput.value = "";
});

// ---------------------------------------------------------------------------
// GIF-picker (praat met de GIPHY-proxy op de server, nooit direct met GIPHY)
// ---------------------------------------------------------------------------

let gifPickerOpen = false;
let gifSearchTimer: ReturnType<typeof setTimeout> | undefined;
/** Hoogste requestnummer dat al getekend is; oudere antwoorden negeren we. */
let gifRequestId = 0;

function renderGifResults(results: GifResult[]): void {
  gifResults.replaceChildren(
    ...results.map((gif) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className =
        "block overflow-hidden rounded-lg bg-slate-100 transition-opacity hover:opacity-70";
      button.title = gif.alt;

      const image = document.createElement("img");
      image.src = gif.preview;
      image.alt = gif.alt;
      image.loading = "lazy";
      image.referrerPolicy = "no-referrer";
      image.className = "block h-20 w-full object-cover";

      button.append(image);
      button.addEventListener("click", () => {
        sendMessage({ t: "gif", url: gif.url, width: gif.width, height: gif.height, alt: gif.alt });
        closeGifPicker();
      });
      return button;
    }),
  );
}

async function loadGifs(query: string): Promise<void> {
  const requestId = ++gifRequestId;
  gifStatus.textContent = "Laden…";
  try {
    const response = await fetch(`/api/gifs?q=${encodeURIComponent(query)}`);
    const body = (await response.json()) as { results?: GifResult[]; error?: string };
    if (requestId !== gifRequestId) return; // Er is alweer nieuwer getypt.
    if (!response.ok || body.results === undefined) {
      gifResults.replaceChildren();
      gifStatus.textContent = body.error ?? "Zoeken mislukt.";
      return;
    }
    renderGifResults(body.results);
    gifStatus.textContent =
      body.results.length === 0 ? "Niets gevonden." : "Klik op een GIF om te versturen.";
  } catch {
    if (requestId !== gifRequestId) return;
    gifStatus.textContent = "Zoeken mislukt.";
  }
}

function closeGifPicker(): void {
  gifPickerOpen = false;
  gifPicker.classList.add("hidden");
}

function openGifPicker(): void {
  gifPickerOpen = true;
  gifPicker.classList.remove("hidden");
  gifSearch.focus();
  void loadGifs(gifSearch.value.trim());
}

gifToggle.addEventListener("click", () => {
  if (gifPickerOpen) closeGifPicker();
  else openGifPicker();
});

gifSearch.addEventListener("input", () => {
  clearTimeout(gifSearchTimer);
  gifSearchTimer = setTimeout(() => void loadGifs(gifSearch.value.trim()), 300);
});

gifSearch.addEventListener("keydown", (event) => {
  if (event.key === "Escape") {
    closeGifPicker();
    chatInput.focus();
  }
});

// Standaard open, behalve op kleine schermen: daar wordt de chat een overlay over het bord.
let chatOpen = window.matchMedia("(min-width: 768px)").matches;

function applyChatVisibility(): void {
  if (!chatOpen) closeGifPicker();
  chatPanel.classList.toggle("hidden", !chatOpen);
  chatPanel.classList.toggle("flex", chatOpen);
  chatToggle.setAttribute("aria-expanded", String(chatOpen));
  chatToggle.classList.toggle("bg-slate-900", chatOpen);
  chatToggle.classList.toggle("text-white", chatOpen);
  chatToggle.classList.toggle("bg-white", !chatOpen);
  chatToggle.classList.toggle("text-slate-600", !chatOpen);
  layout();
}

chatToggle.addEventListener("click", () => {
  chatOpen = !chatOpen;
  applyChatVisibility();
  if (chatOpen) {
    chatLog.scrollTop = chatLog.scrollHeight;
    chatInput.focus();
  }
});

chatClose.addEventListener("click", () => {
  chatOpen = false;
  applyChatVisibility();
});

applyChatVisibility();

// ---------------------------------------------------------------------------
// WebSocket
// ---------------------------------------------------------------------------

function setStatus(text: string | null): void {
  if (text === null) {
    connectionBadge.classList.add("hidden");
    return;
  }
  connectionBadge.textContent = text;
  connectionBadge.classList.remove("hidden");
}

function handleServerMessage(message: ServerMessage): void {
  switch (message.t) {
    case "welcome":
      me = message.you;
      updatePen();
      // Zonder API-key op de server heeft een GIF-knop geen zin.
      gifToggle.classList.toggle("hidden", !message.gifSearch);
      return;
    case "users":
      renderUsers(message.users);
      return;
    case "join":
      playJoinSound();
      return;
    case "prompt":
      promptLabel.textContent = message.text;
      return;
    case "chat":
      appendChatMessage(message.message);
      return;
    case "chatCleared":
      renderChat([]);
      return;
    case "history": {
      renderChat(message.chat);
      strokes.clear();
      for (const stroke of message.strokes) {
        strokes.set(stroke.id, {
          userId: stroke.userId,
          color: stroke.color,
          width: stroke.width,
          points: stroke.points.map((p) => ({ x: p.x, y: p.y })),
        });
      }
      redrawAll();
      return;
    }
    case "cleared": {
      if (message.userId === null) strokes.clear();
      else {
        for (const [key, stroke] of strokes) {
          if (stroke.userId === message.userId) strokes.delete(key);
        }
      }
      redrawAll();
      return;
    }
    case "start": {
      const stroke: Stroke = {
        userId: message.userId,
        color: message.color,
        width: message.w,
        points: [{ x: message.x, y: message.y }],
      };
      strokes.set(message.id, stroke);
      drawDot(stroke, { x: message.x, y: message.y });
      return;
    }
    case "point": {
      const stroke = strokes.get(message.id);
      if (stroke === undefined) return;
      stroke.points.push({ x: message.x, y: message.y });
      drawLastSegment(stroke);
      return;
    }
  }
}

let reconnectDelay = 500;

function connect(name: string): void {
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  const ws = new WebSocket(`${protocol}//${location.host}/ws?name=${encodeURIComponent(name)}`);
  socket = ws;

  ws.addEventListener("open", () => {
    reconnectDelay = 500;
    setStatus(null);
  });

  ws.addEventListener("message", (event) => {
    try {
      handleServerMessage(JSON.parse(String(event.data)) as ServerMessage);
    } catch {
      // Onleesbaar bericht: negeren.
    }
  });

  ws.addEventListener("close", () => {
    if (socket !== ws) return;
    socket = null;
    endStroke();
    renderUsers(me === null ? [] : [me]);
    setStatus("Verbinding verbroken — opnieuw verbinden…");
    setTimeout(() => connect(name), reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay * 2, 8000);
  });

  ws.addEventListener("error", () => ws.close());
}

loginForm.addEventListener("submit", (event) => {
  event.preventDefault();
  const name = nameInput.value.trim();
  if (name.length === 0) return;
  loginOverlay.remove();
  layout();
  connect(name);
});

nameInput.focus();
